import { createContext, useContext, useState, type ReactNode } from "react";

interface AuthState {
  isAuthenticated: boolean;
  partnerName: string;
  email: string;
}

interface AuthContextType {
  auth: AuthState;
  login: (apiTokenId: string, apiClientSecret: string) => boolean;
  logout: () => void;
}

// Sandbox credentials: override via VITE_GRID_API_TOKEN / VITE_GRID_API_SECRET env vars
const ENV_CREDENTIALS = {
  apiTokenId: import.meta.env.VITE_GRID_API_TOKEN || "019c988474f222ba0000358f56486443",
  apiClientSecret: import.meta.env.VITE_GRID_API_SECRET || "aNZTgehvsSY5Xb632uIsxlpAWR-OfV5TdFWVwZ-t014",
};

export const hasEnvCredentials = (): boolean =>
  Boolean(ENV_CREDENTIALS.apiTokenId && ENV_CREDENTIALS.apiClientSecret);

export const getEnvCredentials = () => ENV_CREDENTIALS;

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>(() => {
    // Auto-login for demo mode (only if env credentials are configured)
    const params = new URLSearchParams(window.location.search);
    if (params.get("demo") === "true" && hasEnvCredentials()) {
      sessionStorage.setItem(
        "grid-credentials",
        JSON.stringify(ENV_CREDENTIALS)
      );
      const state: AuthState = {
        isAuthenticated: true,
        partnerName: "Lightspark",
        email: "sandbox",
      };
      sessionStorage.setItem("grid-auth", JSON.stringify(state));
      return state;
    }
    const saved = sessionStorage.getItem("grid-auth");
    if (saved) return JSON.parse(saved);
    return { isAuthenticated: false, partnerName: "", email: "" };
  });

  const login = (apiTokenId: string, apiClientSecret: string): boolean => {
    if (!apiTokenId || !apiClientSecret) return false;
    sessionStorage.setItem(
      "grid-credentials",
      JSON.stringify({ apiTokenId, apiClientSecret })
    );
    const state: AuthState = {
      isAuthenticated: true,
      partnerName: "Lightspark",
      email: "sandbox",
    };
    setAuth(state);
    sessionStorage.setItem("grid-auth", JSON.stringify(state));
    return true;
  };

  const logout = () => {
    setAuth({ isAuthenticated: false, partnerName: "", email: "" });
    sessionStorage.removeItem("grid-auth");
    sessionStorage.removeItem("grid-credentials");
  };

  return (
    <AuthContext.Provider value={{ auth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
